import type { IsoDateTime, PostalAddress } from './common';
import type { TenantId, UserId } from './ids';

export const TENANT_STATUSES = ['onboarding', 'active', 'suspended', 'offboarded'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

/** A seller (satıcı) whose stock and orders the warehouse fulfils. */
export interface Tenant {
  readonly id: TenantId;
  /** 2–10 chars A–Z/0–9, printed on pick lists and labels to tell sellers apart. */
  readonly code: string;
  /** Ticari unvan */
  readonly legalName: string;
  readonly displayName: string;
  /** VKN (10 digits) or TCKN (11 digits) */
  readonly taxNumber: string | null;
  readonly taxOffice: string | null;
  readonly contactName: string | null;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly billingAddress: PostalAddress | null;
  readonly status: TenantStatus;
  readonly settings: TenantSettings;
  readonly createdAt: IsoDateTime;
  readonly updatedAt: IsoDateTime;
}

/** Warehouse-managed per-seller settings (tenants.settings). */
export interface TenantSettings {
  /** FEFO for expiry-tracked stock, FIFO otherwise, unless overridden here. */
  readonly allocationStrategy?: 'fefo' | 'fifo';
  /** Default for products without their own minShelfLifeDays. */
  readonly defaultMinShelfLifeDays?: number;
  /** Reserve stock as soon as an order is imported. */
  readonly autoAllocate?: boolean;
}

export const USER_KINDS = ['tenant', 'operator'] as const;
export type UserKind = (typeof USER_KINDS)[number];

export const TENANT_USER_ROLES = ['owner', 'manager', 'viewer'] as const;
export const OPERATOR_USER_ROLES = ['admin', 'supervisor', 'staff'] as const;
/** Same order as the PostgreSQL enum user_role. */
export const USER_ROLES = [...TENANT_USER_ROLES, ...OPERATOR_USER_ROLES] as const;
export type TenantUserRole = (typeof TENANT_USER_ROLES)[number];
export type OperatorUserRole = (typeof OPERATOR_USER_ROLES)[number];
export type UserRole = TenantUserRole | OperatorUserRole;

export const USER_STATUSES = ['invited', 'active', 'disabled'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

interface UserBase {
  readonly id: UserId;
  readonly email: string;
  readonly fullName: string;
  readonly status: UserStatus;
  readonly lastLoginAt: IsoDateTime | null;
  readonly createdAt: IsoDateTime;
  readonly updatedAt: IsoDateTime;
}

/** Seller staff: sees only its own tenant. */
export interface TenantUser extends UserBase {
  readonly kind: 'tenant';
  readonly tenantId: TenantId;
  readonly role: TenantUserRole;
}

/** Warehouse staff: works across tenants (receiving, picking, packing). */
export interface OperatorUser extends UserBase {
  readonly kind: 'operator';
  readonly tenantId: null;
  readonly role: OperatorUserRole;
}

export type User = TenantUser | OperatorUser;

export const TENANT_STATUS_LABELS: Readonly<Record<TenantStatus, string>> = {
  onboarding: 'Kuruluyor',
  active: 'Aktif',
  suspended: 'Askıda',
  offboarded: 'Ayrıldı',
};

/** Seller roles: owner = firma yöneticisi (tenant admin), manager = firma kullanıcısı, viewer = salt okuma. */
export const USER_ROLE_LABELS: Readonly<Record<UserRole, string>> = {
  owner: 'Firma yöneticisi',
  manager: 'Firma kullanıcısı',
  viewer: 'İzleyici',
  admin: 'Depo yöneticisi',
  supervisor: 'Vardiya sorumlusu',
  staff: 'Depo personeli',
};

export const USER_STATUS_LABELS: Readonly<Record<UserStatus, string>> = { invited: 'Davetli', active: 'Aktif', disabled: 'Pasif' };

/** A user as the user screens list it. */
export interface UserView {
  readonly id: UserId;
  readonly kind: UserKind;
  readonly tenantId: TenantId | null;
  readonly tenantCode: string | null;
  readonly role: UserRole;
  readonly email: string;
  readonly fullName: string;
  readonly status: UserStatus;
  /** the password was set by someone else and must be changed at the next login */
  readonly mustChangePassword: boolean;
  /** signs in with a code from an authenticator app as well */
  readonly twoFactorEnabled: boolean;
  readonly lastLoginAt: IsoDateTime | null;
  readonly createdAt: IsoDateTime;
}

/** One seller on the admin's customer list. */
export interface TenantSummary {
  readonly id: TenantId;
  readonly code: string;
  readonly legalName: string;
  readonly displayName: string;
  readonly taxNumber: string | null;
  readonly status: TenantStatus;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly createdAt: IsoDateTime;
  readonly activeUsers: number;
  readonly products: number;
  readonly unitsInStock: number;
  readonly openOrders: number;
  /** the seller has prices of its own (otherwise the list price applies) */
  readonly customPricing: boolean;
}

export interface TenantDetail extends TenantSummary {
  readonly taxOffice: string | null;
  readonly contactName: string | null;
  readonly billingAddress: PostalAddress | null;
  readonly settings: TenantSettings;
  readonly updatedAt: IsoDateTime;
  /** KVKK, for the warehouse only: absent from the seller's own view of its company */
  readonly personalDataRetention?: TenantRetention;
}

/** How long a seller's closed orders keep their buyer's personal data (warehouse admins set it). */
export interface TenantRetention {
  /** the seller's own period; null: the warehouse default */
  readonly days: number | null;
  /** the warehouse default (PII_RETENTION_DAYS) */
  readonly defaultDays: number;
}

/** VKN is 10 digits; TCKN is 11 digits, starts with a non-zero digit and carries two check digits. */
export function isValidTaxNumber(value: string): boolean {
  if (/^\d{10}$/.test(value)) return true;
  if (!/^[1-9]\d{10}$/.test(value)) return false;
  const d = [...value].map(Number);
  const odd = d[0]! + d[2]! + d[4]! + d[6]! + d[8]!;
  const even = d[1]! + d[3]! + d[5]! + d[7]!;
  const d10 = (((odd * 7 - even) % 10) + 10) % 10;
  const d11 = d.slice(0, 10).reduce((s, n) => s + n, 0) % 10;
  return d[9] === d10 && d[10] === d11;
}

export const MIN_PASSWORD_LENGTH = 10;

/** Why a new password is refused (empty = acceptable); the same rule runs in the browser and the API. */
export function passwordProblems(password: string, email?: string | null): string[] {
  const problems: string[] = [];
  if (password.length < MIN_PASSWORD_LENGTH) problems.push(`En az ${MIN_PASSWORD_LENGTH} karakter olmalı`);
  if (!/\p{L}/u.test(password) || !/\d/.test(password)) problems.push('Harf ve rakam içermeli');
  const local = email?.split('@')[0]?.toLowerCase();
  if (local && local.length >= 4 && password.toLowerCase().includes(local)) problems.push('E-posta adınızı içermemeli');
  return problems;
}

/**
 * Database scope of a request. Each transaction runs `SET LOCAL ROLE app_<scope>`;
 * RLS policies are written against those roles.
 */
export const DB_SCOPES = ['tenant', 'operator', 'system'] as const;
export type DbScope = (typeof DB_SCOPES)[number];

export type RequestContext =
  | { readonly scope: 'tenant'; readonly tenantId: TenantId; readonly userId: UserId | null }
  | { readonly scope: 'operator'; readonly userId: UserId | null }
  | { readonly scope: 'system'; readonly userId: UserId | null; readonly tenantId?: TenantId | null };
